/**
 * Displacement candle (JJ Simon) indicator store
 *
 * Frontend state for the Rust engine's displacement strategy — the aggressive
 * one-sided bars that expand out of the previous candle and close beyond it
 * with almost no wick.
 *
 * Contract (served by utt-engine):
 * - REST  GET  {getApiBaseUrl()}/displacement-markers?symbol=...   -> DisplacementMarker[]
 * - REST  GET/POST {getApiBaseUrl()}/displacement-settings         -> DisplacementSettings
 * - REST  POST {getApiBaseUrl()}/displacement-markers/recalculate  -> { success, markersGenerated }
 * - Socket "displacement-markers-loaded" -> DisplacementMarker[] (batch on connect / recalc)
 * - Socket "displacement-marker"         -> DisplacementMarker (one new detection)
 * - Socket "displacement-marker-update"  -> DisplacementMarker[] (lifecycle upserts)
 *
 * The engine may not be deployed yet — REST 404s / network errors are tolerated
 * with a single console.warn (no spam).
 *
 * ZUSTAND RULE (repo-wide): any selector returning a NEW array/object MUST be
 * wrapped in useShallow — an unwrapped inline filter selector caused a React
 * #185 production crash.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';

// ============================================================================
// Types (mirror the utt-engine contract exactly)
// ============================================================================

export type DisplacementDirection = 'bullish' | 'bearish';

/** Lifecycle status. A missing status means the marker is still live. */
export type DisplacementStatus = 'live' | 'confirmed' | 'failed' | 'expired';

export interface DisplacementMarker {
  /** Stable id: "{symbol}:{timestamp}:{direction}". */
  id: string;
  /** Bar open time of the displacement candle, ms epoch. */
  timestamp: number;
  symbol: string;
  direction: DisplacementDirection;

  // Candle geometry — lets the chart outline the exact bar
  open: number;
  high: number;
  low: number;
  close: number;

  // Measurements
  rangePoints: number;
  bodyPoints: number;
  wickPct: number;
  bodyPct: number;
  /** How close to the leading extreme it closed (100 = closed exactly at it). */
  closeStrength: number;
  /** range / previous range. */
  expansionRatio: number;
  /** range / ATR (0 when ATR unavailable). */
  atrRatio: number;
  /** volume / trailing average volume (0 when unavailable). */
  volumeRatio: number;

  // Grading
  /** Composite 0-100 strength score. */
  score: number;
  /** "A+" | "A" | "B". */
  grade: string;

  // Fair value gap left behind (resolved one candle after detection)
  createdFvg: boolean;
  fvgTop: number | null;
  fvgBottom: number | null;

  // Lifecycle
  status: DisplacementStatus;
  resolvedAt: number | null;
  resolvedPrice: number | null;
  maxFavorablePts: number;
  maxAdversePts: number;

  /** True when a rule (e.g. the ATH guard) vetoed this as a tradeable signal. */
  skipped: boolean;
  skipReason: string | null;
}

export interface DisplacementSettings {
  enabled: boolean;

  // Core shape
  rangeExpansionMult: number;
  maxWickPct: number;
  requireCloseBeyondPrev: boolean;
  closeStrengthPct: number;

  // Absolute size floor, per instrument family
  minRangePoints: number;
  minRangePointsEs: number;
  minRangePointsSil: number;

  // ATR-relative size
  useAtrFilter: boolean;
  atrMult: number;

  // Volume confirmation
  useVolumeFilter: boolean;
  volumeMult: number;
  volumeLookback: number;

  // "No displacement shorts at all-time highs"
  athGuard: boolean;
  athRangePoints: number;

  // Scoring
  weightExpansion: number;
  weightCleanliness: number;
  weightCloseStrength: number;
  weightAtr: number;
  weightVolume: number;
  gradeAPlusScore: number;
  gradeAScore: number;
  minGradeToEmit: string;

  // Lifecycle
  followThroughPts: number;
  failRetracePct: number;
  maxHoldCandles: number;

  // Fair value gap
  detectFvg: boolean;

  // Housekeeping
  lookbackCandles: number;
  maxMarkers: number;
  updatedAt: number;
}

/** Rolling outcome stats, computed client-side from the marker list. */
export interface DisplacementStats {
  live: number;
  confirmed: number;
  failed: number;
  expired: number;
  /** confirmed / (confirmed + failed) * 100; 0 when the denominator is 0. */
  confirmRatePct: number;
}

// ============================================================================
// Constants / helpers
// ============================================================================

const ENABLED_STORAGE_KEY = 'displacement.enabled';
const SHOW_FVG_STORAGE_KEY = 'displacement.showFvg';
const MAX_MARKERS = 200;

export const DEFAULT_DISPLACEMENT_SETTINGS: DisplacementSettings = {
  enabled: true,

  rangeExpansionMult: 1.0,
  maxWickPct: 20,
  requireCloseBeyondPrev: true,
  closeStrengthPct: 70,

  minRangePoints: 10,
  minRangePointsEs: 2.5,
  minRangePointsSil: 0.15,

  useAtrFilter: true,
  atrMult: 1.5,

  useVolumeFilter: false,
  volumeMult: 1.3,
  volumeLookback: 20,

  athGuard: true,
  athRangePoints: 19,

  weightExpansion: 0.25,
  weightCleanliness: 0.2,
  weightCloseStrength: 0.2,
  weightAtr: 0.25,
  weightVolume: 0.1,
  gradeAPlusScore: 75,
  gradeAScore: 55,
  minGradeToEmit: 'B',

  followThroughPts: 10,
  failRetracePct: 100,
  maxHoldCandles: 20,

  detectFvg: true,

  lookbackCandles: 500,
  maxMarkers: 200,
  updatedAt: 0,
};

function loadPersistedBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw === 'true';
  } catch {
    return fallback; // SSR / storage blocked
  }
}

function persistBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // storage blocked — non-fatal
  }
}

// The engine may not be deployed yet: 404s / connection refusals are expected.
// Warn exactly ONCE for the whole session instead of spamming the console.
let warnedUnavailable = false;
function warnUnavailableOnce(what: string): void {
  if (warnedUnavailable) return;
  warnedUnavailable = true;
  console.warn(
    `[Displacement] ${what} unavailable — engine endpoints not deployed yet? (further warnings suppressed)`
  );
}

/** A marker is usable only if it carries the fields the chart needs to draw it. */
function isValidMarker(m: unknown): m is DisplacementMarker {
  if (!m || typeof m !== 'object') return false;
  const c = m as Partial<DisplacementMarker>;
  return (
    typeof c.id === 'string' &&
    typeof c.timestamp === 'number' &&
    typeof c.open === 'number' &&
    typeof c.high === 'number' &&
    typeof c.low === 'number' &&
    typeof c.close === 'number' &&
    (c.direction === 'bullish' || c.direction === 'bearish')
  );
}

/** Insert/replace by id, keep sorted by timestamp, cap at MAX_MARKERS (newest kept). */
function mergeMarker(
  existing: DisplacementMarker[],
  marker: DisplacementMarker
): DisplacementMarker[] {
  const idx = existing.findIndex((m) => m.id === marker.id);
  const next =
    idx === -1 ? [...existing, marker] : existing.map((m, i) => (i === idx ? marker : m));
  next.sort((a, b) => a.timestamp - b.timestamp);
  return next.length > MAX_MARKERS ? next.slice(next.length - MAX_MARKERS) : next;
}

function mergeMany(
  existing: DisplacementMarker[],
  markers: DisplacementMarker[]
): DisplacementMarker[] {
  let next = existing;
  for (const m of markers) {
    if (isValidMarker(m)) next = mergeMarker(next, m);
  }
  return next;
}

/** Count outcomes across a marker list. Expired is excluded from the rate. */
export function computeDisplacementStats(markers: DisplacementMarker[]): DisplacementStats {
  let live = 0;
  let confirmed = 0;
  let failed = 0;
  let expired = 0;
  for (const m of markers) {
    switch (m.status) {
      case 'confirmed':
        confirmed += 1;
        break;
      case 'failed':
        failed += 1;
        break;
      case 'expired':
        expired += 1;
        break;
      default:
        live += 1;
    }
  }
  const decided = confirmed + failed;
  return {
    live,
    confirmed,
    failed,
    expired,
    confirmRatePct: decided === 0 ? 0 : (confirmed / decided) * 100,
  };
}

// ============================================================================
// Store
// ============================================================================

interface DisplacementStoreState {
  /** Indicator visibility toggle (persisted to localStorage). */
  enabled: boolean;
  /** Whether to draw the fair value gap band (persisted to localStorage). */
  showFvg: boolean;
  /** Detected markers, sorted by timestamp, capped at 200. */
  markers: DisplacementMarker[];
  /** Strategy settings mirrored from GET /displacement-settings. */
  settings: DisplacementSettings;

  setEnabled: (enabled: boolean) => void;
  setShowFvg: (show: boolean) => void;
  addMarker: (marker: DisplacementMarker) => void;
  addMarkers: (markers: DisplacementMarker[]) => void;
  replaceMarkers: (markers: DisplacementMarker[]) => void;
  clearMarkers: () => void;

  /** Register the socket listeners (idempotent; retries until the socket exists). */
  initialize: () => void;
  /** Remove the socket listeners registered by initialize(). */
  teardown: () => void;

  fetchMarkers: (symbol?: string) => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateSettings: (partial: Partial<DisplacementSettings>) => Promise<void>;
  recalculate: () => Promise<number>;
}

// --- Socket listener plumbing (module scope so teardown can off() the exact fns) ---

let handlersRegistered = false;
let registerRetryTimer: ReturnType<typeof setInterval> | null = null;

const onDisplacementMarker = (marker: DisplacementMarker): void => {
  if (!isValidMarker(marker)) return;
  useDisplacementStore.getState().addMarker(marker);
};

/** Batch load replaces the set outright — the engine sends the authoritative list. */
const onDisplacementMarkersLoaded = (markers: DisplacementMarker[]): void => {
  if (!Array.isArray(markers)) return;
  useDisplacementStore.getState().replaceMarkers(markers.filter(isValidMarker));
};

/** Lifecycle updates carry full marker objects — same upsert path, merged by id. */
const onDisplacementMarkerUpdate = (markers: DisplacementMarker[]): void => {
  if (!Array.isArray(markers)) return;
  useDisplacementStore.getState().addMarkers(markers.filter(isValidMarker));
};

function stopRegisterRetry(): void {
  if (registerRetryTimer !== null) {
    clearInterval(registerRetryTimer);
    registerRetryTimer = null;
  }
}

function tryRegisterHandlers(): boolean {
  // wsService.on() is a no-op (warn) while the socket is uninitialized; only
  // register once we know the socket exists (isConnected implies it does).
  if (!wsService.isConnected()) return false;
  wsService.on('displacement-marker', onDisplacementMarker);
  wsService.on('displacement-markers-loaded', onDisplacementMarkersLoaded);
  wsService.on('displacement-marker-update', onDisplacementMarkerUpdate);
  handlersRegistered = true;
  return true;
}

export const useDisplacementStore = create<DisplacementStoreState>((set, get) => ({
  enabled: loadPersistedBool(ENABLED_STORAGE_KEY, false),
  showFvg: loadPersistedBool(SHOW_FVG_STORAGE_KEY, true),
  markers: [],
  settings: { ...DEFAULT_DISPLACEMENT_SETTINGS },

  setEnabled: (enabled) => {
    persistBool(ENABLED_STORAGE_KEY, enabled);
    set({ enabled });
  },

  setShowFvg: (showFvg) => {
    persistBool(SHOW_FVG_STORAGE_KEY, showFvg);
    set({ showFvg });
  },

  addMarker: (marker) => set((s) => ({ markers: mergeMarker(s.markers, marker) })),

  addMarkers: (markers) => set((s) => ({ markers: mergeMany(s.markers, markers) })),

  replaceMarkers: (markers) => {
    const next = [...markers].sort((a, b) => a.timestamp - b.timestamp);
    set({ markers: next.length > MAX_MARKERS ? next.slice(next.length - MAX_MARKERS) : next });
  },

  clearMarkers: () => set({ markers: [] }),

  initialize: () => {
    if (handlersRegistered) return;
    if (tryRegisterHandlers()) return;
    // Socket not up yet — retry until it is (bounded; cleared by teardown()).
    if (registerRetryTimer !== null) return;
    let attempts = 0;
    registerRetryTimer = setInterval(() => {
      attempts += 1;
      if (tryRegisterHandlers() || attempts >= 60) {
        stopRegisterRetry();
      }
    }, 1000);
  },

  teardown: () => {
    stopRegisterRetry();
    if (!handlersRegistered) return;
    handlersRegistered = false;
    wsService.off('displacement-marker', onDisplacementMarker);
    wsService.off('displacement-markers-loaded', onDisplacementMarkersLoaded);
    wsService.off('displacement-marker-update', onDisplacementMarkerUpdate);
  },

  fetchMarkers: async (symbol) => {
    try {
      const qs = symbol ? `?symbol=${encodeURIComponent(symbol)}` : '';
      const res = await fetch(`${getApiBaseUrl()}/displacement-markers${qs}`);
      if (!res.ok) {
        warnUnavailableOnce(`GET /displacement-markers (${res.status})`);
        return;
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        get().replaceMarkers(data.filter(isValidMarker));
      }
    } catch {
      warnUnavailableOnce('GET /displacement-markers');
    }
  },

  fetchSettings: async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/displacement-settings`);
      if (!res.ok) {
        warnUnavailableOnce(`GET /displacement-settings (${res.status})`);
        return;
      }
      const data = await res.json();
      if (data && typeof data === 'object') {
        set((s) => ({ settings: { ...s.settings, ...(data as Partial<DisplacementSettings>) } }));
      }
    } catch {
      warnUnavailableOnce('GET /displacement-settings');
    }
  },

  updateSettings: async (partial) => {
    // Optimistic local merge so the UI reflects the change immediately.
    const next = { ...get().settings, ...partial };
    set({ settings: next });
    try {
      const res = await fetch(`${getApiBaseUrl()}/displacement-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) {
        warnUnavailableOnce(`POST /displacement-settings (${res.status})`);
        return;
      }
      // Engine stamps updatedAt — take its version back so the next POST doesn't
      // ship a stale timestamp and stop the live path noticing the change.
      const data = await res.json();
      if (data && typeof data === 'object') {
        set((s) => ({ settings: { ...s.settings, ...(data as Partial<DisplacementSettings>) } }));
      }
    } catch {
      warnUnavailableOnce('POST /displacement-settings');
    }
  },

  recalculate: async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/displacement-markers/recalculate`, {
        method: 'POST',
      });
      if (!res.ok) {
        warnUnavailableOnce(`POST /displacement-markers/recalculate (${res.status})`);
        return 0;
      }
      const data = await res.json();
      // The engine also broadcasts "displacement-markers-loaded", which refreshes
      // the store; the count is returned for UI feedback.
      return typeof data?.markersGenerated === 'number' ? data.markersGenerated : 0;
    } catch {
      warnUnavailableOnce('POST /displacement-markers/recalculate');
      return 0;
    }
  },
}));

// ============================================================================
// Selector hooks
// ============================================================================
// Scalars / stable references can use plain selectors; ANY selector that builds
// a new array/object is wrapped in useShallow (repo rule).

export const useDisplacementEnabled = (): boolean => useDisplacementStore((s) => s.enabled);

export const useDisplacementShowFvg = (): boolean => useDisplacementStore((s) => s.showFvg);

export const useDisplacementSettings = (): DisplacementSettings =>
  useDisplacementStore((s) => s.settings);

export const useDisplacementMarkers = (): DisplacementMarker[] =>
  useDisplacementStore(useShallow((s) => s.markers));

/** Markers filtered to one symbol (base-symbol match, so MNQH6.CME matches MNQ). */
export const useDisplacementMarkersForSymbol = (symbol: string): DisplacementMarker[] =>
  useDisplacementStore(useShallow((s) => s.markers.filter((m) => m.symbol === symbol)));

/** Rolling outcome stats over the current marker list. */
export const useDisplacementStats = (): DisplacementStats =>
  useDisplacementStore(useShallow((s) => computeDisplacementStats(s.markers)));
